import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  Observable,
  retry,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { ProgressStage } from './multi-stage-progress.types';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { StorageApiService } from 'src/app/api/services/storage-api.service';
import { TransferResponse } from 'src/app/api/services/storage-api.service';

export interface CanvaDesign {
  id: string;
  title: string;
  created_at?: number;
  updated_at?: number;
  owner?: {
    user_id: string;
    team_id: string;
  };
  thumbnail?: {
    width: number;
    height: number;
    url: string;
  };
  urls?: {
    edit_url: string;
    view_url: string;
  };
  page_count?: number;
}

export interface CanvaDesignsResponse {
  items: CanvaDesign[];
  continuation?: string;
}
export interface CanvaExportFormat {
  pdf?: {};
  jpg?: {};
  png?: {};
  svg?: {};
  pptx?: {};
  gif?: {};
  mp4?: {};
}

export interface CanvaExportJob {
  job: {
    id: string;
    status: 'in_progress' | 'success' | 'failed';
    urls?: string[];
    error?: {
      code: string;
      message: string;
    };
  };
}

export interface CanvaPngExportOptions {
  type: string;
  export_quality?: string;
  height?: number;
  width?: number;
  lossless?: boolean;
  transparent_background?: boolean;
  as_single_image?: boolean;
  pages?: number[];
}

export interface CanvaMp4ExportOptions {
  type: string;
  quality: string;
  export_quality?: string;
  pages?: number[];
}

export interface CanvaPdfExportOptions {
  type: string;
  size: string;
  export_quality?: string;
}

export interface CanvaImportResult {
  designId: string;
  assetId?: string;
  exportUrl: string;
  designTitle: string;
  designType: string;
  files?: File[];
}

@Injectable({
  providedIn: 'root',
})
export class CanvaConnectApiService {
  private readonly CANVA_API_URL = 'https://api.canva.com/rest';
  private readonly ASSET_TYPE = 'canva';

  constructor(
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private httpClient: HttpClient,
    private storageApiService: StorageApiService,
  ) {}

  getUserProfile(accessToken: string): Observable<any> {
    return this.httpClient.get(`${this.CANVA_API_URL}/v1/users/me/profile`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getDesigns(
    accessToken: string,
    continuation?: string,
    limit: number = 30,
    query?: string,
  ): Observable<CanvaDesignsResponse> {
    const params: any = { limit: limit.toString() };

    if (continuation) {
      params.continuation = continuation;
    }

    if (query?.trim()) {
      params.query = query.trim();
    }

    return this.httpClient
      .get<CanvaDesignsResponse>(`${this.CANVA_API_URL}/v1/designs`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      })
      .pipe(catchError(this.handleError));
  }

  exportOptions(
    type: string,
  ): CanvaMp4ExportOptions | CanvaPdfExportOptions | CanvaPngExportOptions {
    switch (type) {
      case 'mp4':
        return {
          type: 'mp4',
          quality: 'vertical_1080p',
          export_quality: 'regular',
        };

      case 'pdf':
        return {
          type: 'pdf',
          size: 'a4',
          export_quality: 'regular',
        };

      default:
        return {
          type: 'png',
          export_quality: 'regular',
          height: 1080,
        };
    }
  }

  getExportFormats(
    designId: string,
    accessToken: string,
  ): Observable<CanvaExportFormat> {
    return this.httpClient
      .get<{ formats: CanvaExportFormat }>(
        `${this.CANVA_API_URL}/v1/designs/${designId}/export-formats`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .pipe(map((response) => response.formats));
  }

  determineExportFormat(
    formats: CanvaExportFormat,
    pageCount: number,
    exportAsVideo?: boolean,
  ): keyof CanvaExportFormat {
    if (
      formats.pdf &&
      formats.jpg &&
      formats.png &&
      !formats.mp4 &&
      !formats.gif &&
      !formats.pptx
    ) {
      return 'pdf';
    } else if (!formats.png && formats.pdf) {
      return 'pdf';
    } else if (exportAsVideo || (formats.png && pageCount > 1)) {
      return 'mp4';
    } else if (formats.png) {
      return 'png';
    }
  }

  determineExportMimeType(exportFormat: keyof CanvaExportFormat): string {
    switch (exportFormat) {
      case 'mp4':
        return 'video/mp4';
      case 'pdf':
        return 'application/pdf';
    }
    return 'image/png';
  }

  getExportFormat(
    designId: string,
    pageCount: number,
    accessToken: string,
    exportAsVideo?: boolean,
  ): Observable<keyof CanvaExportFormat> {
    return this.getExportFormats(designId, accessToken).pipe(
      map((formats) =>
        this.determineExportFormat(formats, pageCount, exportAsVideo),
      ),
    );
  }

  createExportJob(
    designId: string,
    exportFormat: string,
    accessToken: string,
  ): Observable<CanvaExportJob> {
    return this.httpClient.post<CanvaExportJob>(
      `${this.CANVA_API_URL}/v1/exports`,
      {
        design_id: designId,
        format: this.exportOptions(exportFormat),
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      },
    );
  }

  pollExportJob(
    exportId: string,
    accessToken: string,
    onProgress?: (stage: ProgressStage, progress: number) => void,
  ): Observable<CanvaExportJob> {
    let pollCount = 0;
    const pollInterval = 2000;
    const safetyTimeoutMs = 30 * 60 * 1000; // 30m
    const k = 0.2; // exponential decay rate for progress estimation

    return timer(0, pollInterval).pipe(
      switchMap(() => {
        pollCount++;
        const estimatedProgress = 100 * (1 - Math.exp(-k * pollCount));

        return this.httpClient
          .get<CanvaExportJob>(`${this.CANVA_API_URL}/v1/exports/${exportId}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
          .pipe(
            retry({ count: 20, delay: 2000 }),
            tap((exportJob) => {
              if (onProgress) {
                if (exportJob.job.status === 'in_progress') {
                  onProgress('export', estimatedProgress);
                } else {
                  onProgress('export', 100);
                }
              }
            }),
          );
      }),
      timeout({
        each: safetyTimeoutMs,
        with: () =>
          throwError(
            () =>
              new Error(
                'Export job timed out after 30 minutes. The export is taking longer than expected. Please try again or contact support if the issue persists.',
              ),
          ),
      }),
      filter((exportJob) => exportJob.job.status !== 'in_progress'),
      take(1),
    );
  }

  async downloadExportFiles(
    urls: string[],
    designTitle: string,
    exportFormat: keyof CanvaExportFormat,
    onProgress?: (stage: ProgressStage, progress: number) => void,
  ): Promise<File[]> {
    const files: File[] = [];
    const fileExtension = `.${exportFormat}`;

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const blob = await firstValueFrom(
        this.httpClient.get(url, {
          responseType: 'blob',
        }),
      );

      const filename =
        urls.length > 1
          ? `${designTitle}_${i + 1}${fileExtension}`
          : `${designTitle}${fileExtension}`;

      const file = new File([blob], filename, {
        type: blob.type || this.determineExportMimeType(exportFormat),
      });

      files.push(file);

      if (onProgress) {
        const downloadProgress = ((i + 1) / urls.length) * 100;
        onProgress('download', downloadProgress);
      }
    }

    return files;
  }

  async importDesign(
    design: CanvaDesign,
    accessToken: string,
    exportFormat?: keyof CanvaExportFormat,
    onProgress?: (stage: ProgressStage, progress: number) => void,
  ): Promise<CanvaImportResult> {
    if (!exportFormat) {
      exportFormat = await firstValueFrom(
        this.getExportFormat(design.id, design.page_count, accessToken),
      );
    }

    if (onProgress) {
      onProgress('export', 0);
    }

    const exportJob = await firstValueFrom(
      this.createExportJob(design.id, exportFormat, accessToken),
    );

    if (exportJob.job.error) {
      throw new Error(exportJob.job.error.message);
    }

    const finalExportJob = await firstValueFrom(
      this.pollExportJob(exportJob.job.id, accessToken, onProgress),
    );

    if (finalExportJob.job.status === 'failed') {
      throw new Error(finalExportJob.job.error?.message || 'Export failed');
    }

    if (
      finalExportJob.job.status !== 'success' ||
      !finalExportJob.job.urls ||
      finalExportJob.job.urls.length === 0
    ) {
      throw new Error('Export job did not complete successfully');
    }

    if (exportFormat === 'pdf') {
      const assetId = `${this.userStateService.getUserHash()}_${design.id}`;
      const response = await this.convertPdfsToImages(
        finalExportJob.job.urls,
        assetId,
      );

      await this.pollForPngFile(assetId, onProgress);

      return {
        designId: design.id,
        assetId,
        exportUrl: response.conversionTask.assetPath,
        designTitle: design.title,
        designType: exportFormat,
      };
    } else {
      const files = await this.downloadExportFiles(
        finalExportJob.job.urls,
        design.title,
        exportFormat,
        onProgress,
      );

      return {
        designId: design.id,
        exportUrl: finalExportJob.job.urls[0],
        designTitle: design.title,
        designType: exportFormat,
        files,
      };
    }
  }

  async pollForPngFile(
    assetId: string,
    onProgress: (stage: ProgressStage, progress: number) => void,
    maxAttempts = 20,
    interval = 3000,
  ): Promise<void> {
    const k = 0.2; // exponential decay rate for progress estimation
    if (onProgress) {
      onProgress('encoding', 0);
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const response = await this.storageApiService.getAsset(
        assetId,
        this.ASSET_TYPE,
      );

      const assetsForId = response?.serviceAssets?.[assetId];
      if (Array.isArray(assetsForId) && assetsForId.length > 0) {
        if (onProgress) {
          onProgress('encoding', 100);
        }
        return;
      }

      if (onProgress) {
        const estimatedProgress = 100 * (1 - Math.exp(-k * attempt));
        onProgress('encoding', estimatedProgress);
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    }

    throw new Error(
      `Timed out waiting for converted ${this.ASSET_TYPE} asset: ${assetId}`,
    );
  }

  private async convertPdfsToImages(
    sourceUrls: string[],
    assetId: string,
  ): Promise<TransferResponse> {
    try {
      return await this.storageApiService.transfer(
        sourceUrls,
        `.assets/.asset${assetId}/${this.ASSET_TYPE}/`,
      );
    } catch (error) {
      console.error('Error converting PDF to images:', error);
      throw error;
    }
  }

  private handleError(error: any): Observable<never> {
    console.error('Canva Connect API error:', error);
    return throwError(() => error);
  }

  getDesignById(designId: string, accessToken: string): Promise<CanvaDesign> {
    return firstValueFrom(
      this.httpClient
        .get<{ design: CanvaDesign }>(
          `${this.CANVA_API_URL}/v1/designs/${designId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        )
        .pipe(map((response) => response.design)),
    );
  }
}
